import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import "../CustomPuzzle.css";
import logo from "../../../Assets/newsie-triumphant-192-removebg.png";
import { setModal } from "../../../redux/features/modalSlice";
import { MODALS } from "../../Modal";
import { CONFIG } from "../../../util/constants";

const { SERVER_API } = CONFIG;
export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";
const MAX_NOTES_LENGTH = 8000;

const CreatePuzzlePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authToken = useSelector((state: any) => state.authReducer.authToken);
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = () => {
    try {
      localStorage.setItem(POST_LOGIN_REDIRECT_KEY, "/create");
    } catch (err) {}
    dispatch(setModal(MODALS.LOGIN));
    navigate("/");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!topic.trim() && !notes.trim()) {
      setError("Add a topic or paste some notes.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_API}/custom-puzzles`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-auth-token": authToken },
        body: JSON.stringify({ topic: topic.trim(), notes: notes.trim() }),
      });
      if (res.status === 401 || res.status === 403) {
        setError("Your session expired. Log in again to make a puzzle.");
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.message || "Something went wrong making your puzzle.");
        return;
      }
      navigate(`/p/${body.id}`);
    } catch (err) {
      setError("Couldn't reach Newsie. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-puzzle-page">
      <div className="create-puzzle-card">
        <Link to="/" className="create-puzzle-brand">
          <img src={logo} alt="" width={32} height={32} />
          <span>Newsie</span>
        </Link>
        <h1>Make a study puzzle</h1>
        <p className="create-puzzle-subtitle">
          Turn anything you're learning into a Newsie-style puzzle: key facts
          squeezed into three words for you to rebuild.
        </p>

        {!authToken ? (
          <div className="create-puzzle-login">
            <p>Log in or sign up (it's free) to make your own puzzles.</p>
            <button className="custom-puzzle-button primary" onClick={handleLogin}>
              Log in or sign up
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label htmlFor="puzzle-topic">What do you want to remember?</label>
            <input
              id="puzzle-topic"
              type="text"
              maxLength={120}
              placeholder="e.g. Cell biology, the French Revolution, Spanish verbs"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={loading}
            />
            <label htmlFor="puzzle-notes">
              Paste your notes <span className="create-puzzle-optional">(optional, most accurate)</span>
            </label>
            <textarea
              id="puzzle-notes"
              rows={7}
              maxLength={MAX_NOTES_LENGTH}
              placeholder="Paste class notes, a study guide or a textbook section. We'll only use facts from what you paste."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />
            {error && <div className="create-puzzle-error">{error}</div>}
            <button className="custom-puzzle-button primary" type="submit" disabled={loading}>
              {loading ? "Building your puzzle…" : "Create puzzle"}
            </button>
            <p className="create-puzzle-fineprint">
              Puzzles are made by AI, so double-check facts before an exam. Anyone with the link can play.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreatePuzzlePage;
